import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTenantContext } from 'src/context/useTenantContext';
import {
  tenantStorefront,
  tenantStorefrontBranchOrdering,
  tenantStorefrontBranches,
  tenantStorefrontPreviewLink,
  tenantStorefrontPublish,
  tenantStorefrontSlugAvailable,
  tenantStorefrontUnpublish,
  tenantStorefrontUpdate,
} from 'src/services/apis/tenantRequests';
import {
  RestaurantBranchEntity,
  RestaurantStorefrontEntity,
  StorefrontBranchOrderingDto,
  StorefrontUpdateDto,
} from 'src/services/apis/types';
import { QUERIES } from 'src/utils/react-query';

/**
 * The generated entity now carries `publicUrl`, `customCss` and `analytics` — the hand-declared
 * overrides they used to need are gone, which is what the previous note here asked for once the
 * client was regenerated.
 *
 * `analytics` is the one that still needs narrowing. It is a jsonb column, so codegen types it
 * `{ [x: string]: any }`; the screens need the real shape to read `consentRequired` and the three
 * ids without casting at every site. `Omit` + re-declare is the narrowing, not a second source of
 * truth — `StorefrontAnalyticsShape` mirrors the server's `StorefrontAnalytics`.
 */
export type StorefrontWithPublicUrl = Omit<RestaurantStorefrontEntity, 'analytics'> & {
  analytics?: StorefrontAnalyticsShape | null;
};

export type StorefrontAnalyticsShape = {
  ga4MeasurementId: string | null;
  metaPixelId: string | null;
  tiktokPixelId: string | null;
  consentRequired: boolean;
};

export const useStorefront = () => {
  const { orgSlug, restaurantId } = useTenantContext();
  return useQuery({
    queryKey: [QUERIES.STOREFRONT, orgSlug, restaurantId],
    // The cast narrows `analytics` from the jsonb `{ [x: string]: any }` codegen emits to the
    // shape the server actually sends (`StorefrontAnalytics`). One assertion at the boundary
    // rather than one per read site.
    queryFn: () => tenantStorefront({ orgSlug, restaurantId }) as Promise<StorefrontWithPublicUrl>,
  });
};

export const useStorefrontSlugAvailability = () => {
  const { orgSlug, restaurantId } = useTenantContext();
  // Returns a function rather than a query: the caller decides when to ask, so the editor can
  // debounce it and skip the request entirely for a slug the user has not changed.
  return (slug: string) => tenantStorefrontSlugAvailable({ orgSlug, restaurantId }, slug);
};

function useInvalidateStorefront() {
  const queryClient = useQueryClient();
  const { orgSlug, restaurantId } = useTenantContext();
  return () => queryClient.refetchQueries([QUERIES.STOREFRONT, orgSlug, restaurantId]);
}

export const useUpdateStorefrontMutation = () => {
  const { orgSlug, restaurantId } = useTenantContext();
  const invalidate = useInvalidateStorefront();
  return useMutation((payload: StorefrontUpdateDto) => tenantStorefrontUpdate({ orgSlug, restaurantId }, payload), {
    onSuccess: invalidate,
  });
};

export const usePublishStorefrontMutation = () => {
  const { orgSlug, restaurantId } = useTenantContext();
  const invalidate = useInvalidateStorefront();
  return useMutation(() => tenantStorefrontPublish({ orgSlug, restaurantId }), {
    onSuccess: invalidate,
  });
};

export const useUnpublishStorefrontMutation = () => {
  const { orgSlug, restaurantId } = useTenantContext();
  const invalidate = useInvalidateStorefront();
  return useMutation(() => tenantStorefrontUnpublish({ orgSlug, restaurantId }), {
    onSuccess: invalidate,
  });
};

/**
 * The branches this restaurant can turn into public ordering channels.
 *
 * Read from the STOREFRONT controller rather than the branches API, so the list and the endpoint
 * that flips the switch agree on scope and capability without the screen joining two responses.
 */
export const useStorefrontBranches = () => {
  const { orgSlug, restaurantId } = useTenantContext();
  return useQuery({
    queryKey: [QUERIES.STOREFRONT_BRANCHES, orgSlug, restaurantId],
    queryFn: (): Promise<RestaurantBranchEntity[]> => tenantStorefrontBranches({ orgSlug, restaurantId }),
  });
};

/**
 * Public ordering for one branch.
 *
 * Invalidates the BRANCH list, not the storefront row: these two columns live on
 * `RestaurantBranches`, and the storefront query would not show the change. The server
 * revalidates the public site itself, because `/menu` renders from this.
 */
export const useStorefrontBranchOrderingMutation = () => {
  const { orgSlug, restaurantId } = useTenantContext();
  const queryClient = useQueryClient();
  return useMutation(
    ({ branchId, ...payload }: StorefrontBranchOrderingDto & { branchId: string }) =>
      tenantStorefrontBranchOrdering({ orgSlug, restaurantId }, branchId, payload),
    {
      onSuccess: () => queryClient.refetchQueries([QUERIES.STOREFRONT_BRANCHES, orgSlug, restaurantId]),
    }
  );
};

/**
 * The preview link mutates nothing, so it deliberately does NOT invalidate the storefront query —
 * asking for a signed URL does not change the row. It takes no payload and resolves to
 * `{ url, expiresAt }`.
 */
export const useStorefrontPreviewLinkMutation = () => {
  const { orgSlug, restaurantId } = useTenantContext();
  return useMutation(() => tenantStorefrontPreviewLink({ orgSlug, restaurantId }));
};
